// ApocalypseSettings — extracted from Settings.jsx as the P0 refactor.
// All state remains owned by Settings.jsx; this component is pure UI
// and receives ~110 props (54 state values + 54 setters + a few helpers
// like `navigate`, `rehearseValue/Suit`). The verbose prop list is the
// trade-off for keeping a single source of truth for state.

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ChevronDown,
  Image as ImageIcon,
  Lock,
  Palette,
  RotateCcw,
  Trash2,
} from "lucide-react";

import {
  // Constants
  APOCALYPSE_FX_PERM_TYPES,
  DEFAULT_APOCALYPSE_ERROR_BODY,
  DEFAULT_APOCALYPSE_ERROR_TITLE,
  DEFAULT_APOCALYPSE_EXTRAS_COUNT,
  DEFAULT_APOCALYPSE_EXTRAS_INTERVAL,
  DEFAULT_APOCALYPSE_EXTRAS_TITLES,
  DEFAULT_APOCALYPSE_FLASH_COLORS,
  DEFAULT_APOCALYPSE_FLICKER_MS,
  DEFAULT_APOCALYPSE_FX_CLIPBOARD_TEMPLATE,
  DEFAULT_APOCALYPSE_FX_PERM_TYPE,
  DEFAULT_APOCALYPSE_FX_SPEECH_PITCH,
  DEFAULT_APOCALYPSE_FX_SPEECH_RATE,
  DEFAULT_APOCALYPSE_FX_SPEECH_TEMPLATE,
  DEFAULT_APOCALYPSE_FX_THEME_COLORS,
  DEFAULT_APOCALYPSE_FX_THEME_INTERVAL,
  DEFAULT_APOCALYPSE_POST_WIPE_DELAY_MS,
  DEFAULT_APOCALYPSE_ROW_CYCLE,
  DEFAULT_APOCALYPSE_ROW_FRAME,
  DEFAULT_APOCALYPSE_ROW_STAGGER,
  DEFAULT_APOCALYPSE_SECOND_TEMPLATE,
  DEFAULT_APOCALYPSE_SHAKE_COUNT,
  DEFAULT_APOCALYPSE_SHAKE_THRESHOLD,
  DEFAULT_APOCALYPSE_TEMPLATE,
  DEFAULT_APOCALYPSE_WIPE_FRAME_MS,
  MAX_APOCALYPSE_CUE_DOT_LEAD_SEC,
  MAX_APOCALYPSE_CUE_DOT_OPACITY_PCT,
  MAX_APOCALYPSE_CUE_DOT_SIZE_PX,
  MAX_APOCALYPSE_DELAY,
  MAX_APOCALYPSE_EXTRAS_COUNT,
  MAX_APOCALYPSE_EXTRAS_INTERVAL,
  MAX_APOCALYPSE_FLASH_COUNT,
  MAX_APOCALYPSE_FLASH_DELAY_MS,
  MAX_APOCALYPSE_FLASH_DURATION_MS,
  MAX_APOCALYPSE_FLASH_GAP_MS,
  MAX_APOCALYPSE_FLICKER_MS,
  MAX_APOCALYPSE_FX_SPEECH_PITCH,
  MAX_APOCALYPSE_FX_SPEECH_RATE,
  MAX_APOCALYPSE_FX_THEME_INTERVAL,
  MAX_APOCALYPSE_POST_WIPE_DELAY_MS,
  MAX_APOCALYPSE_ROW_CYCLE,
  MAX_APOCALYPSE_ROW_FRAME,
  MAX_APOCALYPSE_ROW_STAGGER,
  MAX_APOCALYPSE_SHAKE_COUNT,
  MAX_APOCALYPSE_SHAKE_THRESHOLD,
  MAX_APOCALYPSE_WIPE_FRAME_MS,
  MIN_APOCALYPSE_CUE_DOT_LEAD_SEC,
  MIN_APOCALYPSE_CUE_DOT_OPACITY_PCT,
  MIN_APOCALYPSE_CUE_DOT_SIZE_PX,
  MIN_APOCALYPSE_DELAY,
  MIN_APOCALYPSE_EXTRAS_COUNT,
  MIN_APOCALYPSE_EXTRAS_INTERVAL,
  MIN_APOCALYPSE_FLASH_COUNT,
  MIN_APOCALYPSE_FLASH_DELAY_MS,
  MIN_APOCALYPSE_FLASH_DURATION_MS,
  MIN_APOCALYPSE_FLASH_GAP_MS,
  MIN_APOCALYPSE_FLICKER_MS,
  MIN_APOCALYPSE_FX_SPEECH_PITCH,
  MIN_APOCALYPSE_FX_SPEECH_RATE,
  MIN_APOCALYPSE_FX_THEME_INTERVAL,
  MIN_APOCALYPSE_POST_WIPE_DELAY_MS,
  MIN_APOCALYPSE_ROW_CYCLE,
  MIN_APOCALYPSE_ROW_FRAME,
  MIN_APOCALYPSE_ROW_STAGGER,
  MIN_APOCALYPSE_SHAKE_COUNT,
  MIN_APOCALYPSE_SHAKE_THRESHOLD,
  MIN_APOCALYPSE_WIPE_FRAME_MS,
  // Helpers
  playErrorTritone,
  playAppNotification,
  playIphoneLockClick,
  setApocalypseArmed,
  setApocalypseRehearsing,
  speakRevealFx,
  writeClipboardFx,
} from "../lib/mr";

export default function ApocalypseSettings({
  // Core
  apocEnabled, setApocEnabled,
  apocLength, setApocLength,
  apocDelay, setApocDelay,
  apocTemplate, setApocTemplate,
  apocWallpaper, setApocWallpaper,
  apocResetPreset, setApocResetPreset,
  apocFlash1, setApocFlash1,
  apocFlash2, setApocFlash2,
  apocFlash3, setApocFlash3,
  apocFlash4, setApocFlash4,
  apocFlashCount, setApocFlashCount,
  apocFlashDelayMs, setApocFlashDelayMs,
  apocFlashDurationMs, setApocFlashDurationMs,
  apocFlashGapMs, setApocFlashGapMs,
  apocAutoMotion, setApocAutoMotion,
  apocShakeThreshold, setApocShakeThreshold,
  apocShakeCount, setApocShakeCount,
  apocMotionStatus, setApocMotionStatus,
  // Wipe + spawn cadence
  apocWipeFrameMs, setApocWipeFrameMs,
  apocPostWipeDelayMs, setApocPostWipeDelayMs,
  apocExtrasCount, setApocExtrasCount,
  apocExtrasInterval, setApocExtrasInterval,
  apocExtrasTitles, setApocExtrasTitles,
  // Flicker / row cadence
  apocFlickerMs, setApocFlickerMs,
  apocRowStagger, setApocRowStagger,
  apocRowCycle, setApocRowCycle,
  apocRowFrame, setApocRowFrame,
  // Wipe / error / shake mode
  apocWipeMode, setApocWipeMode,
  apocErrorEnabled, setApocErrorEnabled,
  apocErrorTitle, setApocErrorTitle,
  apocErrorBody, setApocErrorBody,
  apocErrorSoundVolume, setApocErrorSoundVolume,
  apocVibrationEnabled, setApocVibrationEnabled,
  apocVibrationVolume, setApocVibrationVolume,
  apocSecondTemplate, setApocSecondTemplate,
  apocShakeMode, setApocShakeMode,
  // Push + scroll + post-restore flash
  apocPushEnabled, setApocPushEnabled,
  apocPushSoundEnabled, setApocPushSoundEnabled,
  apocScrollSpeedMs, setApocScrollSpeedMs,
  apocRestoreFlashEnabled, setApocRestoreFlashEnabled,
  apocRestoreFlashMs, setApocRestoreFlashMs,
  // FX (permission / speech / clipboard / theme)
  apocFxFeedback, setApocFxFeedback,
  apocFxPermEnabled, setApocFxPermEnabled,
  apocFxPermType, setApocFxPermType,
  apocFxSpeechEnabled, setApocFxSpeechEnabled,
  apocFxSpeechTemplate, setApocFxSpeechTemplate,
  apocFxSpeechVoice, setApocFxSpeechVoice,
  apocFxSpeechRate, setApocFxSpeechRate,
  apocFxSpeechPitch, setApocFxSpeechPitch,
  apocFxClipboardEnabled, setApocFxClipboardEnabled,
  apocFxClipboardTemplate, setApocFxClipboardTemplate,
  apocFxThemeEnabled, setApocFxThemeEnabled,
  apocFxThemeInterval, setApocFxThemeInterval,
  apocFxThemeColors, setApocFxThemeColors,
  // Cue dot
  apocCueDotSizePx, setApocCueDotSizePx,
  apocCueDotColor, setApocCueDotColor,
  apocCueDotOpacityPct, setApocCueDotOpacityPct,
  apocCueDotLeadSec, setApocCueDotLeadSec,
  // Cinematic advanced disclosure
  apocCinematicOpen, setApocCinematicOpen,
  // Rehearse helpers (lives in parent because rehearse value/suit are
  // shared with the swiper rehearsal card too)
  rehearseValue, setRehearseValue,
  rehearseSuit, setRehearseSuit,
  // Wallpaper picker handler (file → base64 → setApocWallpaper)
  onWallpaperPick,
  // Voice list for FX speech selector (populated by parent useEffect)
  voicesList,
  // Router
  navigate,
}) {
  return (
        <Card
          className="mt-5 border-white/10 bg-white/[0.04] backdrop-blur p-5 sm:p-6 rounded-2xl"
          data-testid="apocalypse-card"
        >
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-white text-base sm:text-lg font-semibold">
                <Lock className="w-4 h-4" style={{ color: "#e85a64" }} /> Notes Apocalypse
              </div>
              <p className="text-xs text-white/55 mt-1.5 leading-relaxed">
                Long-press the &ldquo;X notes&rdquo; counter to open a fake
                iOS lockscreen with coded passcode entry. See <span className="text-white/75">The Manual</span> for the encoding.
              </p>
            </div>
            <Switch
              checked={apocEnabled}
              onCheckedChange={setApocEnabled}
              className="data-[state=checked]:bg-[#a11421]"
              data-testid="apocalypse-toggle"
            />
          </div>

          {apocEnabled && (
            <div className="mt-5 space-y-5">
              {/* Routing legend (passcode coding) */}
              <div className="bg-black/30 border border-white/5 rounded-xl p-3">
                <p className="text-[11px] uppercase tracking-[0.32em] text-white/55 mb-2">
                  Passcode coding
                </p>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-[12px] text-white/70 leading-tight">
                  <div>01 &nbsp;Ace</div><div>02 &nbsp;Two</div>
                  <div>03 &nbsp;Three</div><div>04 &nbsp;Four</div>
                  <div>05 &nbsp;Five</div><div>06 &nbsp;Six</div>
                  <div>07 &nbsp;Seven</div><div>08 &nbsp;Eight</div>
                  <div>09 &nbsp;Nine</div><div>10 &nbsp;Ten</div>
                  <div>11 &nbsp;Jack</div><div>12 &nbsp;Queen</div>
                  <div>13 &nbsp;King</div><div />
                </div>
                <div className="mt-3 pt-3 border-t border-white/[0.06] grid grid-cols-2 gap-x-4 gap-y-1.5 text-[12px] text-white/70 leading-tight">
                  <div>1 &nbsp;♠ Spades</div><div>2 &nbsp;♥ Hearts</div>
                  <div>3 &nbsp;♣ Clubs</div><div>4 &nbsp;♦ Diamonds</div>
                </div>
                <p className="text-[11px] text-white/45 mt-3 leading-relaxed">
                  Example &middot; <span className="font-mono text-white/65">0414</span> in 4-digit mode = Four of Spades (last digit wildcard).
                </p>
              </div>

              {/* Passcode length */}
              <div>
                <Label className="text-[11px] uppercase tracking-[0.32em] text-white/55">
                  Passcode length
                </Label>
                <Select value={apocLength} onValueChange={setApocLength}>
                  <SelectTrigger
                    className="mt-3 h-10 bg-black/30 border-white/10 text-white"
                    data-testid="apocalypse-length-trigger"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="4" data-testid="apocalypse-length-4">4 digits (iOS classic)</SelectItem>
                    <SelectItem value="6" data-testid="apocalypse-length-6">6 digits (iOS default)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-white/45 mt-2 leading-relaxed">
                  Match your real phone&rsquo;s passcode length so the keypad
                  doesn&rsquo;t tell.
                </p>
              </div>

              {/* Delay */}
              <div>
                <Label htmlFor="apoc-delay-input" className="text-[11px] uppercase tracking-[0.32em] text-white/55">
                  Trigger delay
                </Label>
                <div className="mt-3 flex items-center gap-3">
                  <Input
                    id="apoc-delay-input"
                    type="number"
                    inputMode="numeric"
                    min={MIN_APOCALYPSE_DELAY}
                    max={MAX_APOCALYPSE_DELAY}
                    value={apocDelay}
                    onChange={(e) => setApocDelay(e.target.value)}
                    className="h-10 bg-black/30 border-white/10 text-white w-24"
                    data-testid="apocalypse-delay-input"
                  />
                  <span className="text-sm text-white/55">seconds after unlock</span>
                </div>
                <p className="text-xs text-white/45 mt-2 leading-relaxed">
                  How long the phone looks normal in the spectator&rsquo;s
                  hand before the glitch begins. 0 = instant, max 100s.
                </p>
              </div>

              {/* Post-shake reset preset (replaces the old fake-reboot
                  sequence — iOS won't let a PWA hide the system clock /
                  battery, which gave the reboot away every time). */}
              <div className="mt-6 rounded-xl border border-white/10 bg-black/20 p-4">
                <div className="text-[11px] uppercase tracking-[0.32em] text-white/55">
                  After spectator shakes
                </div>
                <div className="mt-3 flex gap-2" data-testid="apocalypse-reset-preset">
                  {[
                    ["instant", "Instant"],
                    ["flash", "Flash"],
                  ].map(([val, label]) => {
                    const active = apocResetPreset === val;
                    return (
                      <button
                        key={val}
                        type="button"
                        onClick={() => setApocResetPreset(val)}
                        className={`px-4 h-9 rounded-md text-xs border transition-colors ${
                          active
                            ? "bg-white/15 border-white/40 text-white"
                            : "bg-black/30 border-white/10 text-white/65 hover:bg-white/10"
                        }`}
                        data-testid={`apocalypse-reset-preset-${val}`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-white/45 mt-2 leading-relaxed">
                  <span className="text-white/70">Instant</span> snaps
                  straight back to the clean decoy folder.{" "}
                  <span className="text-white/70">Flash</span> holds the
                  empty beat, then fires a quick white camera-pop
                  before resetting.
                </p>

                {/* Flash-only knobs ------------------------------ */}
                {apocResetPreset === "flash" && (
                  <div className="mt-4 grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="apoc-flash-delay" className="text-[11px] uppercase tracking-[0.32em] text-white/55">
                        Delay (ms)
                      </Label>
                      <Input
                        id="apoc-flash-delay"
                        type="number"
                        inputMode="numeric"
                        min={MIN_APOCALYPSE_FLASH_DELAY_MS}
                        max={MAX_APOCALYPSE_FLASH_DELAY_MS}
                        value={apocFlashDelayMs}
                        onChange={(e) => setApocFlashDelayMs(e.target.value)}
                        className="mt-2 h-10 bg-black/30 border-white/10 text-white"
                        data-testid="apocalypse-flash-delay-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="apoc-flash-count" className="text-[11px] uppercase tracking-[0.32em] text-white/55">
                        Flashes
                      </Label>
                      <Input
                        id="apoc-flash-count"
                        type="number"
                        inputMode="numeric"
                        min={MIN_APOCALYPSE_FLASH_COUNT}
                        max={MAX_APOCALYPSE_FLASH_COUNT}
                        value={apocFlashCount}
                        onChange={(e) => setApocFlashCount(e.target.value)}
                        className="mt-2 h-10 bg-black/30 border-white/10 text-white"
                        data-testid="apocalypse-flash-count-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="apoc-flash-duration" className="text-[11px] uppercase tracking-[0.32em] text-white/55">
                        On (ms)
                      </Label>
                      <Input
                        id="apoc-flash-duration"
                        type="number"
                        inputMode="numeric"
                        min={MIN_APOCALYPSE_FLASH_DURATION_MS}
                        max={MAX_APOCALYPSE_FLASH_DURATION_MS}
                        value={apocFlashDurationMs}
                        onChange={(e) => setApocFlashDurationMs(e.target.value)}
                        className="mt-2 h-10 bg-black/30 border-white/10 text-white"
                        data-testid="apocalypse-flash-duration-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="apoc-flash-gap" className="text-[11px] uppercase tracking-[0.32em] text-white/55">
                        Gap (ms)
                      </Label>
                      <Input
                        id="apoc-flash-gap"
                        type="number"
                        inputMode="numeric"
                        min={MIN_APOCALYPSE_FLASH_GAP_MS}
                        max={MAX_APOCALYPSE_FLASH_GAP_MS}
                        value={apocFlashGapMs}
                        onChange={(e) => setApocFlashGapMs(e.target.value)}
                        className="mt-2 h-10 bg-black/30 border-white/10 text-white"
                        data-testid="apocalypse-flash-gap-input"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Pre-wipe cue dot ("secret indicator") --------------
                  A barely-visible signal the magician glances at to
                  know when the wipe is about to begin. Every knob is
                  tunable so it can be invisible to the spectator under
                  the magician's specific lighting/distance. */}
              <div className="mt-6 rounded-xl border border-white/10 bg-black/20 p-4">
                <div className="text-[11px] uppercase tracking-[0.32em] text-white/55">
                  Secret indicator (cue dot)
                </div>
                <p className="text-xs text-white/45 mt-2 leading-relaxed">
                  A tiny dot lights up <span className="font-mono text-white/65">lead</span> seconds
                  before the wipe so you know exactly when to look up.
                  Sits to the right of the &ldquo;N notes&rdquo; counter at the
                  top &mdash; spectator shouldn&rsquo;t notice it at default
                  3 px / 7 % opacity.
                </p>

                {/* Size / Color / Opacity / Lead — compact grid */}
                <div className="mt-4 grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="apoc-cue-size" className="text-[11px] uppercase tracking-[0.32em] text-white/55">
                      Size (px)
                    </Label>
                    <Input
                      id="apoc-cue-size"
                      type="number"
                      inputMode="numeric"
                      min={MIN_APOCALYPSE_CUE_DOT_SIZE_PX}
                      max={MAX_APOCALYPSE_CUE_DOT_SIZE_PX}
                      value={apocCueDotSizePx}
                      onChange={(e) => setApocCueDotSizePx(e.target.value)}
                      className="mt-2 h-10 bg-black/30 border-white/10 text-white"
                      data-testid="apocalypse-cue-dot-size-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="apoc-cue-color" className="text-[11px] uppercase tracking-[0.32em] text-white/55">
                      Color
                    </Label>
                    <div className="mt-2 flex items-center gap-2">
                      <Input
                        id="apoc-cue-color"
                        type="color"
                        value={apocCueDotColor}
                        onChange={(e) => setApocCueDotColor(e.target.value)}
                        className="h-10 w-12 bg-black/30 border-white/10 p-1"
                        data-testid="apocalypse-cue-dot-color-input"
                      />
                      <Input
                        type="text"
                        value={apocCueDotColor}
                        onChange={(e) => setApocCueDotColor(e.target.value)}
                        className="h-10 bg-black/30 border-white/10 text-white font-mono"
                        data-testid="apocalypse-cue-dot-color-hex"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="apoc-cue-opacity" className="text-[11px] uppercase tracking-[0.32em] text-white/55">
                      Opacity (%)
                    </Label>
                    <Input
                      id="apoc-cue-opacity"
                      type="number"
                      inputMode="numeric"
                      min={MIN_APOCALYPSE_CUE_DOT_OPACITY_PCT}
                      max={MAX_APOCALYPSE_CUE_DOT_OPACITY_PCT}
                      value={apocCueDotOpacityPct}
                      onChange={(e) => setApocCueDotOpacityPct(e.target.value)}
                      className="mt-2 h-10 bg-black/30 border-white/10 text-white"
                      data-testid="apocalypse-cue-dot-opacity-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="apoc-cue-lead" className="text-[11px] uppercase tracking-[0.32em] text-white/55">
                      Lead (sec)
                    </Label>
                    <Input
                      id="apoc-cue-lead"
                      type="number"
                      inputMode="numeric"
                      min={MIN_APOCALYPSE_CUE_DOT_LEAD_SEC}
                      max={MAX_APOCALYPSE_CUE_DOT_LEAD_SEC}
                      value={apocCueDotLeadSec}
                      onChange={(e) => setApocCueDotLeadSec(e.target.value)}
                      className="mt-2 h-10 bg-black/30 border-white/10 text-white"
                      data-testid="apocalypse-cue-dot-lead-input"
                    />
                  </div>
                </div>
              </div>

              {/* Sentence template */}
              <div>
                <Label htmlFor="apoc-template-input" className="text-[11px] uppercase tracking-[0.32em] text-white/55">
                  Glitched sentence (xxx) = card
                </Label>
                <Input
                  id="apoc-template-input"
                  type="text"
                  value={apocTemplate}
                  onChange={(e) => setApocTemplate(e.target.value)}
                  placeholder={DEFAULT_APOCALYPSE_TEMPLATE}
                  className="mt-3 h-10 bg-black/30 border-white/10 text-white placeholder:text-white/30"
                  data-testid="apocalypse-template-input"
                />
                <p className="text-xs text-white/45 mt-2 leading-relaxed">
                  The note titles all rewrite to this sentence.
                  <span className="font-mono text-white/65"> (xxx)</span>
                  &nbsp;gets replaced by the card name (e.g. &ldquo;four of diamonds&rdquo;).
                </p>
              </div>

              {/* Wallpaper upload */}
              <div>
                <Label className="text-[11px] uppercase tracking-[0.32em] text-white/55">
                  Lockscreen wallpaper
                </Label>
                <div className="mt-3 flex items-center gap-3">
                  <div
                    className="w-16 h-16 rounded-xl border border-white/10 overflow-hidden shrink-0 bg-black/40"
                    style={
                      apocWallpaper
                        ? { backgroundImage: `url(${apocWallpaper})`, backgroundSize: "cover", backgroundPosition: "center" }
                        : undefined
                    }
                    data-testid="apocalypse-wallpaper-preview"
                  >
                    {!apocWallpaper && (
                      <div className="w-full h-full flex items-center justify-center text-white/40">
                        <ImageIcon className="w-5 h-5" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 flex flex-wrap gap-2">
                    <label className="inline-flex items-center px-3 h-9 rounded-md bg-white/10 hover:bg-white/15 text-white text-sm cursor-pointer">
                      Upload image
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          const f = e.target.files && e.target.files[0];
                          if (f) onWallpaperPick(f);
                          e.target.value = "";
                        }}
                        data-testid="apocalypse-wallpaper-input"
                      />
                    </label>
                    {apocWallpaper && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setApocWallpaper("")}
                        className="h-9 px-3 text-white/65 hover:text-white hover:bg-white/5"
                        data-testid="apocalypse-wallpaper-clear"
                      >
                        <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Clear
                      </Button>
                    )}
                  </div>
                </div>
                <p className="text-xs text-white/45 mt-2 leading-relaxed">
                  Use a screenshot of your real lockscreen so the trick
                  phone looks like yours. Stored locally on this device.
                </p>
              </div>

              {/* ── Cinematic glitch sub-panel ───────────────────────── */}
              <div className="rounded-xl border border-white/[0.06] bg-black/20 overflow-hidden">
                <button
                  type="button"
                  onClick={() => setApocCinematicOpen((v) => !v)}
                  className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-white/[0.03] transition-colors"
                  data-testid="apocalypse-cinematic-toggle"
                  aria-expanded={apocCinematicOpen}
                >
                  <span className="flex items-center gap-2 text-white/85 text-sm font-medium">
                    <Palette className="w-3.5 h-3.5 text-[#e85a64]" />
                    Cinematic glitch
                    <span className="text-[10px] uppercase tracking-[0.32em] text-white/35 ml-1">
                      advanced
                    </span>
                  </span>
                  <ChevronDown
                    className={`w-4 h-4 text-white/55 transition-transform ${apocCinematicOpen ? "rotate-180" : ""}`}
                  />
                </button>

                {apocCinematicOpen && (
                  <div className="px-4 pb-5 pt-1 space-y-5 border-t border-white/[0.06]">
                    {/* Phase 0 — Wipe (reverse-hack delete of preset note titles) */}
                    <div>
                      <p className="text-[11px] uppercase tracking-[0.32em] text-white/55 mb-1">
                        Phase 0 · Wipe
                      </p>
                      <p className="text-xs text-white/45 mb-3 leading-relaxed">
                        Reverse-hack delete &mdash; every preset note
                        title drops one character at a time
                        simultaneously until they&rsquo;re gone. Then a
                        beat of silence before the regen begins.
                      </p>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label htmlFor="apoc-wipe-frame" className="text-[10px] uppercase tracking-[0.28em] text-white/50">
                            Char delete (ms)
                          </Label>
                          <Input
                            id="apoc-wipe-frame"
                            type="number"
                            inputMode="numeric"
                            min={MIN_APOCALYPSE_WIPE_FRAME_MS}
                            max={MAX_APOCALYPSE_WIPE_FRAME_MS}
                            value={apocWipeFrameMs}
                            onChange={(e) => setApocWipeFrameMs(e.target.value)}
                            className="mt-2 h-10 bg-black/30 border-white/10 text-white"
                            data-testid="apocalypse-wipe-frame"
                          />
                        </div>
                        <div>
                          <Label htmlFor="apoc-post-wipe-delay" className="text-[10px] uppercase tracking-[0.28em] text-white/50">
                            Post-wipe pause (ms)
                          </Label>
                          <Input
                            id="apoc-post-wipe-delay"
                            type="number"
                            inputMode="numeric"
                            min={MIN_APOCALYPSE_POST_WIPE_DELAY_MS}
                            max={MAX_APOCALYPSE_POST_WIPE_DELAY_MS}
                            value={apocPostWipeDelayMs}
                            onChange={(e) => setApocPostWipeDelayMs(e.target.value)}
                            className="mt-2 h-10 bg-black/30 border-white/10 text-white"
                            data-testid="apocalypse-post-wipe-delay"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Phase 1 — Extras spawn */}
                    <div>
                      <p className="text-[11px] uppercase tracking-[0.32em] text-white/55 mb-1">
                        Phase 1 · Extras spawn
                      </p>
                      <p className="text-xs text-white/45 mb-3 leading-relaxed">
                        Fake notes the phone &ldquo;leaks&rdquo; before the
                        flicker hits. Higher count + tighter interval = more
                        chaotic feed.
                      </p>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label htmlFor="apoc-extras-count" className="text-[10px] uppercase tracking-[0.28em] text-white/50">
                            Count
                          </Label>
                          <Input
                            id="apoc-extras-count"
                            type="number"
                            inputMode="numeric"
                            min={MIN_APOCALYPSE_EXTRAS_COUNT}
                            max={MAX_APOCALYPSE_EXTRAS_COUNT}
                            value={apocExtrasCount}
                            onChange={(e) => setApocExtrasCount(e.target.value)}
                            className="mt-2 h-10 bg-black/30 border-white/10 text-white"
                            data-testid="apocalypse-extras-count"
                          />
                        </div>
                        <div>
                          <Label htmlFor="apoc-extras-interval" className="text-[10px] uppercase tracking-[0.28em] text-white/50">
                            Interval (ms)
                          </Label>
                          <Input
                            id="apoc-extras-interval"
                            type="number"
                            inputMode="numeric"
                            min={MIN_APOCALYPSE_EXTRAS_INTERVAL}
                            max={MAX_APOCALYPSE_EXTRAS_INTERVAL}
                            value={apocExtrasInterval}
                            onChange={(e) => setApocExtrasInterval(e.target.value)}
                            className="mt-2 h-10 bg-black/30 border-white/10 text-white"
                            data-testid="apocalypse-extras-interval"
                          />
                        </div>
                      </div>

                      <div className="mt-3">
                        <Label htmlFor="apoc-extras-titles" className="text-[10px] uppercase tracking-[0.28em] text-white/50">
                          Title pool (one per line)
                        </Label>
                        <textarea
                          id="apoc-extras-titles"
                          rows={6}
                          value={apocExtrasTitles}
                          onChange={(e) => setApocExtrasTitles(e.target.value)}
                          placeholder={DEFAULT_APOCALYPSE_EXTRAS_TITLES.join("\n")}
                          className="mt-2 w-full bg-black/30 border border-white/10 rounded-md text-white text-sm placeholder:text-white/30 px-3 py-2 leading-relaxed font-mono resize-y min-h-[140px]"
                          data-testid="apocalypse-extras-titles"
                        />
                        <p className="text-[11px] text-white/40 mt-1.5 leading-relaxed">
                          Spawned in random order. Empty lines are ignored.
                          Leave blank to keep using the built-in pool.
                        </p>
                      </div>
                    </div>

                    {/* Phase 2 — Flicker */}
                    <div>
                      <p className="text-[11px] uppercase tracking-[0.32em] text-white/55 mb-1">
                        Phase 2 · Flicker
                      </p>
                      <p className="text-xs text-white/45 mb-3 leading-relaxed">
                        Full-screen color flash. Pick a palette to match
                        your character &mdash; reds for horror, greens for
                        Matrix, brand colors for corporate.
                      </p>
                      <div>
                        <Label htmlFor="apoc-flicker-ms" className="text-[10px] uppercase tracking-[0.28em] text-white/50">
                          Duration (ms)
                        </Label>
                        <Input
                          id="apoc-flicker-ms"
                          type="number"
                          inputMode="numeric"
                          min={MIN_APOCALYPSE_FLICKER_MS}
                          max={MAX_APOCALYPSE_FLICKER_MS}
                          value={apocFlickerMs}
                          onChange={(e) => setApocFlickerMs(e.target.value)}
                          className="mt-2 h-10 bg-black/30 border-white/10 text-white w-32"
                          data-testid="apocalypse-flicker-ms"
                        />
                      </div>
                      <div className="mt-4 grid grid-cols-4 gap-3">
                        {[
                          { val: apocFlash1, set: setApocFlash1, tid: "apocalypse-flash-1", label: "Flash 1" },
                          { val: apocFlash2, set: setApocFlash2, tid: "apocalypse-flash-2", label: "Flash 2" },
                          { val: apocFlash3, set: setApocFlash3, tid: "apocalypse-flash-3", label: "Flash 3" },
                          { val: apocFlash4, set: setApocFlash4, tid: "apocalypse-flash-4", label: "Flash 4" },
                        ].map((f) => (
                          <label key={f.tid} className="flex flex-col items-start gap-1.5">
                            <span className="text-[10px] uppercase tracking-[0.28em] text-white/50">
                              {f.label}
                            </span>
                            <span
                              className="relative w-full h-10 rounded-md border border-white/10 overflow-hidden cursor-pointer"
                              style={{ background: f.val }}
                            >
                              <input
                                type="color"
                                value={f.val}
                                onChange={(e) => f.set(e.target.value)}
                                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                data-testid={f.tid}
                                aria-label={f.label}
                              />
                            </span>
                            <span className="font-mono text-[10px] text-white/50">
                              {f.val}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Phase 3 — Resolve cadence */}
                    <div>
                      <p className="text-[11px] uppercase tracking-[0.32em] text-white/55 mb-1">
                        Phase 3 · Resolve
                      </p>
                      <p className="text-xs text-white/45 mb-3 leading-relaxed">
                        How titles scramble into the forced sentence.
                        Stagger = delay between rows, cycle = how long
                        each row scrambles, frame = single-character speed.
                      </p>
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <Label htmlFor="apoc-row-stagger" className="text-[10px] uppercase tracking-[0.28em] text-white/50">
                            Stagger (ms)
                          </Label>
                          <Input
                            id="apoc-row-stagger"
                            type="number"
                            inputMode="numeric"
                            min={MIN_APOCALYPSE_ROW_STAGGER}
                            max={MAX_APOCALYPSE_ROW_STAGGER}
                            value={apocRowStagger}
                            onChange={(e) => setApocRowStagger(e.target.value)}
                            className="mt-2 h-10 bg-black/30 border-white/10 text-white"
                            data-testid="apocalypse-row-stagger"
                          />
                        </div>
                        <div>
                          <Label htmlFor="apoc-row-cycle" className="text-[10px] uppercase tracking-[0.28em] text-white/50">
                            Cycle (ms)
                          </Label>
                          <Input
                            id="apoc-row-cycle"
                            type="number"
                            inputMode="numeric"
                            min={MIN_APOCALYPSE_ROW_CYCLE}
                            max={MAX_APOCALYPSE_ROW_CYCLE}
                            value={apocRowCycle}
                            onChange={(e) => setApocRowCycle(e.target.value)}
                            className="mt-2 h-10 bg-black/30 border-white/10 text-white"
                            data-testid="apocalypse-row-cycle"
                          />
                        </div>
                        <div>
                          <Label htmlFor="apoc-row-frame" className="text-[10px] uppercase tracking-[0.28em] text-white/50">
                            Frame (ms)
                          </Label>
                          <Input
                            id="apoc-row-frame"
                            type="number"
                            inputMode="numeric"
                            min={MIN_APOCALYPSE_ROW_FRAME}
                            max={MAX_APOCALYPSE_ROW_FRAME}
                            value={apocRowFrame}
                            onChange={(e) => setApocRowFrame(e.target.value)}
                            className="mt-2 h-10 bg-black/30 border-white/10 text-white"
                            data-testid="apocalypse-row-frame"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Native iOS FX — abuse real system APIs */}
                    <div className="space-y-4">
                      <div>
                        <p className="text-[11px] uppercase tracking-[0.32em] text-white/55 mb-1">
                          Native iOS FX
                        </p>
                        <p className="text-xs text-white/45 leading-relaxed">
                          Four independent abuses of real iOS / Safari
                          APIs. Each one summons authentic system UI
                          rather than CSS look-alikes. Toggle the ones
                          you want — they layer freely.
                        </p>
                      </div>

                      {/* FX 1 — Permission prompt theatre */}
                      <div className="rounded-lg border border-white/[0.06] bg-black/20 p-3 space-y-2">
                        <div className="flex items-center justify-between">
                          <Label className="text-xs text-white/85 flex items-center gap-2">
                            <span>📷</span> Permission prompt theatre
                          </Label>
                          <Switch
                            checked={apocFxPermEnabled}
                            onCheckedChange={setApocFxPermEnabled}
                            data-testid="apocalypse-fx-perm-toggle"
                          />
                        </div>
                        <p className="text-[11px] text-white/45 leading-relaxed">
                          Mid-glitch, summons the real iOS permission
                          sheet (camera / mic / notifications / location).
                          The system dialog itself does the heavy lifting.
                          One-shot per origin — once tapped, iOS won&rsquo;t
                          ask again until permissions are reset.
                        </p>
                        {apocFxPermEnabled && (
                          <Select value={apocFxPermType} onValueChange={setApocFxPermType}>
                            <SelectTrigger className="h-9 bg-black/30 border-white/10 text-white text-xs" data-testid="apocalypse-fx-perm-type">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-neutral-900 border-white/10 text-white">
                              {APOCALYPSE_FX_PERM_TYPES.map((t) => (
                                <SelectItem key={t} value={t}>{t}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </div>

                      {/* FX 2 — Speech synthesis reveal */}
                      <div className="rounded-lg border border-white/[0.06] bg-black/20 p-3 space-y-2">
                        <div className="flex items-center justify-between">
                          <Label className="text-xs text-white/85 flex items-center gap-2">
                            <span>🔊</span> Spoken reveal
                          </Label>
                          <Switch
                            checked={apocFxSpeechEnabled}
                            onCheckedChange={setApocFxSpeechEnabled}
                            data-testid="apocalypse-fx-speech-toggle"
                          />
                        </div>
                        <p className="text-[11px] text-white/45 leading-relaxed">
                          Robotic voice speaks the card aloud as the
                          rows resolve. Use <span className="font-mono text-white/65">(xxx)</span>{" "}
                          or <span className="font-mono text-white/65">{`{card}`}</span> in your template
                          to interpolate the card name.
                        </p>
                        {apocFxSpeechEnabled && (
                          <>
                            <textarea
                              value={apocFxSpeechTemplate}
                              onChange={(e) => setApocFxSpeechTemplate(e.target.value)}
                              placeholder={DEFAULT_APOCALYPSE_FX_SPEECH_TEMPLATE}
                              rows={2}
                              className="w-full bg-black/30 border border-white/10 rounded-md text-white text-sm placeholder:text-white/30 px-3 py-2 leading-relaxed"
                              data-testid="apocalypse-fx-speech-template"
                            />
                            <Select value={apocFxSpeechVoice || "__default"} onValueChange={(v) => setApocFxSpeechVoice(v === "__default" ? "" : v)}>
                              <SelectTrigger className="h-9 bg-black/30 border-white/10 text-white text-xs" data-testid="apocalypse-fx-speech-voice">
                                <SelectValue placeholder="System default voice" />
                              </SelectTrigger>
                              <SelectContent className="bg-neutral-900 border-white/10 text-white max-h-72">
                                <SelectItem value="__default">System default</SelectItem>
                                {voicesList.map((v) => (
                                  <SelectItem key={v.voiceURI} value={v.voiceURI}>
                                    {v.name} ({v.lang})
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <Label className="text-[10px] uppercase tracking-[0.28em] text-white/50">Rate</Label>
                                <Input
                                  type="number"
                                  step="0.05"
                                  min={MIN_APOCALYPSE_FX_SPEECH_RATE}
                                  max={MAX_APOCALYPSE_FX_SPEECH_RATE}
                                  value={apocFxSpeechRate}
                                  onChange={(e) => setApocFxSpeechRate(e.target.value)}
                                  className="mt-1.5 h-9 bg-black/30 border-white/10 text-white"
                                  data-testid="apocalypse-fx-speech-rate"
                                />
                              </div>
                              <div>
                                <Label className="text-[10px] uppercase tracking-[0.28em] text-white/50">Pitch</Label>
                                <Input
                                  type="number"
                                  step="0.05"
                                  min={MIN_APOCALYPSE_FX_SPEECH_PITCH}
                                  max={MAX_APOCALYPSE_FX_SPEECH_PITCH}
                                  value={apocFxSpeechPitch}
                                  onChange={(e) => setApocFxSpeechPitch(e.target.value)}
                                  className="mt-1.5 h-9 bg-black/30 border-white/10 text-white"
                                  data-testid="apocalypse-fx-speech-pitch"
                                />
                              </div>
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              onClick={() => {
                                const txt = (apocFxSpeechTemplate || "")
                                  .replace(/\(xxx\)/gi, "four of spades")
                                  .replace(/\{card\}/gi, "four of spades");
                                speakRevealFx(txt, {
                                  voiceUri: apocFxSpeechVoice,
                                  rate: parseFloat(apocFxSpeechRate),
                                  pitch: parseFloat(apocFxSpeechPitch),
                                });
                              }}
                              className="w-full text-white/70 hover:text-white hover:bg-white/5 rounded-md h-8 text-xs border border-white/10"
                              data-testid="apocalypse-fx-speech-test"
                            >
                              ▶ Test speech with sample card
                            </Button>
                          </>
                        )}
                      </div>

                      {/* FX 3 — Clipboard hijack */}
                      <div className="rounded-lg border border-white/[0.06] bg-black/20 p-3 space-y-2">
                        <div className="flex items-center justify-between">
                          <Label className="text-xs text-white/85 flex items-center gap-2">
                            <span>📋</span> Clipboard hijack
                          </Label>
                          <Switch
                            checked={apocFxClipboardEnabled}
                            onCheckedChange={setApocFxClipboardEnabled}
                            data-testid="apocalypse-fx-clipboard-toggle"
                          />
                        </div>
                        <p className="text-[11px] text-white/45 leading-relaxed">
                          On unlock, silently writes the card text to the
                          clipboard. After the trick, the spectator can
                          paste anywhere (Notes, Messages, Safari URL bar)
                          and the card reappears. Use <span className="font-mono text-white/65">(xxx)</span> or <span className="font-mono text-white/65">{`{card}`}</span>.
                        </p>
                        {apocFxClipboardEnabled && (
                          <>
                            <Input
                              value={apocFxClipboardTemplate}
                              onChange={(e) => setApocFxClipboardTemplate(e.target.value)}
                              placeholder={DEFAULT_APOCALYPSE_FX_CLIPBOARD_TEMPLATE}
                              className="h-9 bg-black/30 border-white/10 text-white text-sm"
                              data-testid="apocalypse-fx-clipboard-template"
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              onClick={async () => {
                                const txt = (apocFxClipboardTemplate || "")
                                  .replace(/\(xxx\)/gi, "four of spades")
                                  .replace(/\{card\}/gi, "four of spades");
                                const ok = await writeClipboardFx(txt);
                                setApocFxFeedback(ok ? "✓ Written to clipboard" : "✗ Clipboard blocked");
                                setTimeout(() => setApocFxFeedback(""), 2500);
                              }}
                              className="w-full text-white/70 hover:text-white hover:bg-white/5 rounded-md h-8 text-xs border border-white/10"
                              data-testid="apocalypse-fx-clipboard-test"
                            >
                              ▶ Test clipboard write
                            </Button>
                            {apocFxFeedback && (
                              <p className="text-[11px] text-emerald-300 text-center" data-testid="apocalypse-fx-feedback">{apocFxFeedback}</p>
                            )}
                          </>
                        )}
                      </div>

                      {/* FX 4 — Theme-color status-bar flicker */}
                      <div className="rounded-lg border border-white/[0.06] bg-black/20 p-3 space-y-2">
                        <div className="flex items-center justify-between">
                          <Label className="text-xs text-white/85 flex items-center gap-2">
                            <span>📱</span> Status-bar color flicker
                          </Label>
                          <Switch
                            checked={apocFxThemeEnabled}
                            onCheckedChange={setApocFxThemeEnabled}
                            data-testid="apocalypse-fx-theme-toggle"
                          />
                        </div>
                        <p className="text-[11px] text-white/45 leading-relaxed">
                          Cycles the iOS native status-bar background
                          color during the flicker phase. Works best when
                          the app is installed to the home screen (PWA
                          mode), where iOS honors <span className="font-mono text-white/65">theme-color</span>.
                        </p>
                        {apocFxThemeEnabled && (
                          <>
                            <div>
                              <Label className="text-[10px] uppercase tracking-[0.28em] text-white/50">
                                Cycle (comma-separated hex)
                              </Label>
                              <Input
                                value={apocFxThemeColors}
                                onChange={(e) => setApocFxThemeColors(e.target.value)}
                                placeholder={DEFAULT_APOCALYPSE_FX_THEME_COLORS.join(", ")}
                                className="mt-1.5 h-9 bg-black/30 border-white/10 text-white text-sm font-mono"
                                data-testid="apocalypse-fx-theme-colors"
                              />
                            </div>
                            <div>
                              <Label className="text-[10px] uppercase tracking-[0.28em] text-white/50">
                                Interval (ms)
                              </Label>
                              <Input
                                type="number"
                                min={MIN_APOCALYPSE_FX_THEME_INTERVAL}
                                max={MAX_APOCALYPSE_FX_THEME_INTERVAL}
                                value={apocFxThemeInterval}
                                onChange={(e) => setApocFxThemeInterval(e.target.value)}
                                className="mt-1.5 h-9 bg-black/30 border-white/10 text-white"
                                data-testid="apocalypse-fx-theme-interval"
                              />
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Reset gesture — shake-to-dismiss */}
                    <div>
                      <p className="text-[11px] uppercase tracking-[0.32em] text-white/55 mb-1">
                        Reset gesture · Shake
                      </p>
                      <p className="text-xs text-white/45 mb-3 leading-relaxed">
                        The spectator shakes the phone to dismiss the
                        glitch and return to clean notes. Threshold =
                        how vigorous each shake peak must be; Peaks =
                        how many peaks within 1&nbsp;second to trigger.
                      </p>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label htmlFor="apoc-shake-threshold" className="text-[10px] uppercase tracking-[0.28em] text-white/50">
                            Threshold (m/s²)
                          </Label>
                          <Input
                            id="apoc-shake-threshold"
                            type="number"
                            inputMode="numeric"
                            min={MIN_APOCALYPSE_SHAKE_THRESHOLD}
                            max={MAX_APOCALYPSE_SHAKE_THRESHOLD}
                            value={apocShakeThreshold}
                            onChange={(e) => setApocShakeThreshold(e.target.value)}
                            className="mt-2 h-10 bg-black/30 border-white/10 text-white"
                            data-testid="apocalypse-shake-threshold"
                          />
                        </div>
                        <div>
                          <Label htmlFor="apoc-shake-count" className="text-[10px] uppercase tracking-[0.28em] text-white/50">
                            Peaks needed
                          </Label>
                          <Input
                            id="apoc-shake-count"
                            type="number"
                            inputMode="numeric"
                            min={MIN_APOCALYPSE_SHAKE_COUNT}
                            max={MAX_APOCALYPSE_SHAKE_COUNT}
                            value={apocShakeCount}
                            onChange={(e) => setApocShakeCount(e.target.value)}
                            className="mt-2 h-10 bg-black/30 border-white/10 text-white"
                            data-testid="apocalypse-shake-count"
                          />
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={async () => {
                          try {
                            const DM = typeof window !== "undefined" ? window.DeviceMotionEvent : null;
                            if (!DM) { setApocMotionStatus("unsupported"); return; }
                            if (typeof DM.requestPermission === "function") {
                              setApocMotionStatus("prompting");
                              const res = await DM.requestPermission();
                              setApocMotionStatus(res === "granted" ? "granted" : "denied");
                            } else {
                              // Android / older iOS — no prompt needed.
                              setApocMotionStatus("granted");
                            }
                          } catch {
                            setApocMotionStatus("denied");
                          }
                        }}
                        className="mt-3 w-full text-white/70 hover:text-white hover:bg-white/5 rounded-lg h-9 text-xs border border-white/10"
                        data-testid="apocalypse-shake-grant"
                      >
                        {apocMotionStatus === "granted" && "✓ Motion access granted"}
                        {apocMotionStatus === "denied" && "✗ Motion access denied — tap to retry"}
                        {apocMotionStatus === "unsupported" && "Motion sensor unavailable on this device"}
                        {apocMotionStatus === "prompting" && "Waiting for permission…"}
                        {apocMotionStatus === "idle" && "Grant motion access (iOS, once per device)"}
                      </Button>
                      <p className="text-[11px] text-white/40 mt-2 leading-relaxed">
                        iOS requires explicit permission from a user tap.
                        Grant it once on your performance phone during
                        prep — Android phones work without prompting.
                      </p>

                      {/* Auto-request motion on first lockscreen tap.
                          When ON (default), the spectator's first touch
                          of the lockscreen silently re-asks iOS for
                          motion permission. If iOS still remembers the
                          grant from a prior session the call resolves
                          invisibly; otherwise the prompt appears at the
                          natural moment of unlocking. Either way the
                          magician never has to pre-tap "Grant motion"
                          before each spectator. */}
                      <div
                        className="mt-3 flex items-center justify-between gap-3 rounded-lg border border-white/10 bg-black/20 px-3 py-3"
                        data-testid="apocalypse-auto-motion-row"
                      >
                        <div className="flex-1">
                          <p className="text-xs text-white font-medium">
                            Auto-request on lockscreen tap
                          </p>
                          <p className="text-[11px] text-white/45 leading-relaxed mt-0.5">
                            Silently re-ask iOS the first time the
                            spectator touches the lockscreen. No prompt
                            appears if permission is already granted.
                          </p>
                        </div>
                        <Switch
                          checked={apocAutoMotion}
                          onCheckedChange={setApocAutoMotion}
                          data-testid="apocalypse-auto-motion-switch"
                        />
                      </div>

                      {/* iOS "Shake to Undo" conflict warning — one-time
                          system-level setup the magician must do on the
                          performance phone, since the iOS Accessibility
                          undo prompt intercepts every shake before
                          Safari/PWA can see it. */}
                      <div
                        className="mt-4 rounded-lg border border-amber-400/30 bg-amber-400/[0.06] px-3 py-3"
                        data-testid="apocalypse-shake-ios-notice"
                      >
                        <p className="text-[10px] uppercase tracking-[0.32em] text-amber-300/90 mb-1.5 font-semibold">
                          ⚠ One-time iOS setup
                        </p>
                        <p className="text-xs text-amber-50/85 leading-relaxed">
                          iPhones ship with <span className="font-semibold">Shake to Undo</span> on,
                          which catches the shake before this app does
                          and pops a &ldquo;Herstel typen / Undo Typing&rdquo;
                          dialog. Turn it off on your performance phone:
                        </p>
                        <ol className="mt-2 space-y-0.5 text-xs text-amber-50/80 leading-relaxed list-decimal list-inside">
                          <li>Open iOS <span className="font-semibold">Settings</span></li>
                          <li>
                            <span className="font-semibold">Accessibility</span> →{" "}
                            <span className="font-semibold">Touch</span>
                          </li>
                          <li>
                            Scroll to <span className="font-semibold">Shake to Undo</span> &mdash;{" "}
                            toggle <span className="font-semibold">OFF</span>
                          </li>
                        </ol>
                        <p className="mt-2 text-[11px] text-amber-50/60 leading-relaxed">
                          Set once, stays off. The spectator never needs
                          to know. Android performance phones are
                          unaffected.
                        </p>
                      </div>
                    </div>

                    {/* Reset to defaults */}
                    <Button
                      variant="ghost"
                      onClick={() => {
                        setApocWipeFrameMs(String(DEFAULT_APOCALYPSE_WIPE_FRAME_MS));
                        setApocPostWipeDelayMs(String(DEFAULT_APOCALYPSE_POST_WIPE_DELAY_MS));
                        setApocShakeThreshold(String(DEFAULT_APOCALYPSE_SHAKE_THRESHOLD));
                        setApocShakeCount(String(DEFAULT_APOCALYPSE_SHAKE_COUNT));
                        // Native FX
                        setApocFxPermEnabled(false);
                        setApocFxPermType(DEFAULT_APOCALYPSE_FX_PERM_TYPE);
                        setApocFxSpeechEnabled(false);
                        setApocFxSpeechTemplate(DEFAULT_APOCALYPSE_FX_SPEECH_TEMPLATE);
                        setApocFxSpeechVoice("");
                        setApocFxSpeechRate(String(DEFAULT_APOCALYPSE_FX_SPEECH_RATE));
                        setApocFxSpeechPitch(String(DEFAULT_APOCALYPSE_FX_SPEECH_PITCH));
                        setApocFxClipboardEnabled(false);
                        setApocFxClipboardTemplate(DEFAULT_APOCALYPSE_FX_CLIPBOARD_TEMPLATE);
                        setApocFxThemeEnabled(false);
                        setApocFxThemeColors(DEFAULT_APOCALYPSE_FX_THEME_COLORS.join(", "));
                        setApocFxThemeInterval(String(DEFAULT_APOCALYPSE_FX_THEME_INTERVAL));
                        setApocExtrasCount(String(DEFAULT_APOCALYPSE_EXTRAS_COUNT));
                        setApocExtrasInterval(String(DEFAULT_APOCALYPSE_EXTRAS_INTERVAL));
                        setApocExtrasTitles(DEFAULT_APOCALYPSE_EXTRAS_TITLES.join("\n"));
                        setApocFlickerMs(String(DEFAULT_APOCALYPSE_FLICKER_MS));
                        setApocFlash1(DEFAULT_APOCALYPSE_FLASH_COLORS[0]);
                        setApocFlash2(DEFAULT_APOCALYPSE_FLASH_COLORS[1]);
                        setApocFlash3(DEFAULT_APOCALYPSE_FLASH_COLORS[2]);
                        setApocFlash4(DEFAULT_APOCALYPSE_FLASH_COLORS[3]);
                        setApocRowStagger(String(DEFAULT_APOCALYPSE_ROW_STAGGER));
                        setApocRowCycle(String(DEFAULT_APOCALYPSE_ROW_CYCLE));
                        setApocRowFrame(String(DEFAULT_APOCALYPSE_ROW_FRAME));
                      }}
                      className="w-full text-white/55 hover:text-white hover:bg-white/5 rounded-lg h-9 text-xs"
                      data-testid="apocalypse-cinematic-reset"
                    >
                      <RotateCcw className="w-3 h-3 mr-1.5" /> Reset cinematic to defaults
                    </Button>
                  </div>
                )}
              </div>

              {/* ---------- Pass-4 controls (Wipe mode · Error dialog ·
                  Second prediction · Shake mode) ---------- */}
              <div
                className="mt-6 space-y-4 rounded-xl border border-white/10 bg-black/20 p-4"
                data-testid="apocalypse-pass4-block"
              >
                <div>
                  <div className="text-[11px] uppercase tracking-[0.32em] text-white/55">
                    Wipe style
                  </div>
                  <p className="text-xs text-white/45 mt-2 leading-relaxed">
                    How the initial preset notes disappear when the
                    trick starts.
                  </p>
                  <Select value={apocWipeMode} onValueChange={setApocWipeMode}>
                    <SelectTrigger
                      className="mt-3 bg-black/30 border-white/10 text-white h-10"
                      data-testid="apocalypse-wipe-mode"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-neutral-900 border-white/10 text-white">
                      <SelectItem value="one-by-one" className="text-white focus:bg-white/10 focus:text-white">
                        Letters delete one by one, then notes vanish
                      </SelectItem>
                      <SelectItem value="flash" className="text-white focus:bg-white/10 focus:text-white">
                        Everything disappears in one flash
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="border-t border-white/10 pt-4">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex-1">
                      <div className="text-[11px] uppercase tracking-[0.32em] text-white/55">
                        Post-reveal error dialog
                      </div>
                      <p className="text-xs text-white/45 mt-2 leading-relaxed">
                        After the resolve scrolls to the top, drop an
                        iOS-style error alert with sound. Background
                        keeps glitching, taps + scroll blocked, only
                        clock-swipe still encodes &mdash; perfect window
                        for the second card.
                      </p>
                    </div>
                    <Switch
                      checked={apocErrorEnabled}
                      onCheckedChange={setApocErrorEnabled}
                      data-testid="apocalypse-error-enabled-switch"
                    />
                  </div>
                  {apocErrorEnabled && (
                    <div className="mt-3 space-y-3">
                      <div>
                        <Label htmlFor="apoc-error-title" className="text-[10px] uppercase tracking-[0.28em] text-white/50">
                          Title
                        </Label>
                        <Input
                          id="apoc-error-title"
                          value={apocErrorTitle}
                          onChange={(e) => setApocErrorTitle(e.target.value)}
                          placeholder={DEFAULT_APOCALYPSE_ERROR_TITLE}
                          maxLength={80}
                          className="mt-2 h-10 bg-black/30 border-white/10 text-white"
                          data-testid="apocalypse-error-title-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="apoc-error-body" className="text-[10px] uppercase tracking-[0.28em] text-white/50">
                          Body
                        </Label>
                        <Input
                          id="apoc-error-body"
                          value={apocErrorBody}
                          onChange={(e) => setApocErrorBody(e.target.value)}
                          placeholder={DEFAULT_APOCALYPSE_ERROR_BODY}
                          maxLength={240}
                          className="mt-2 h-10 bg-black/30 border-white/10 text-white"
                          data-testid="apocalypse-error-body-input"
                        />
                      </div>
                      <div className="grid grid-cols-[1fr_auto] gap-3 items-end">
                        <div>
                          <Label htmlFor="apoc-error-volume" className="text-[10px] uppercase tracking-[0.28em] text-white/50">
                            Sound volume (0–100)
                          </Label>
                          <Input
                            id="apoc-error-volume"
                            type="number"
                            inputMode="numeric"
                            min={0}
                            max={100}
                            value={apocErrorSoundVolume}
                            onChange={(e) => setApocErrorSoundVolume(e.target.value)}
                            className="mt-2 h-10 bg-black/30 border-white/10 text-white"
                            data-testid="apocalypse-error-sound-volume"
                          />
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() => {
                            const v = Math.max(0, Math.min(100, parseInt(apocErrorSoundVolume, 10) || 0));
                            try {
                              playErrorTritone({ volume: v / 100 });
                            } catch { /* ignore */ }
                          }}
                          className="h-10 px-4 text-white/70 hover:text-white hover:bg-white/5 border border-white/10 rounded-lg text-xs"
                          data-testid="apocalypse-error-sound-test"
                        >
                          Test sound
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                <div className="border-t border-white/10 pt-4">
                  <div className="text-[11px] uppercase tracking-[0.32em] text-white/55">
                    Vibration buzz
                  </div>
                  <p className="text-xs text-white/45 mt-2 leading-relaxed">
                    Loops a phone-in-silent-mode buzz sample under all
                    other sounds for the entire haunt phase. Cuts dead
                    the instant the error dialog raises. Works on iOS
                    Safari where the real Vibration API is blocked.
                  </p>
                  <div className="mt-3 flex items-center justify-between rounded-lg border border-white/10 bg-black/20 px-3 py-2.5">
                    <Label className="text-sm text-white/80" htmlFor="apoc-vibration-enabled">
                      Enable vibration buzz
                    </Label>
                    <Switch
                      id="apoc-vibration-enabled"
                      checked={apocVibrationEnabled}
                      onCheckedChange={setApocVibrationEnabled}
                      data-testid="apocalypse-vibration-enabled"
                    />
                  </div>
                  {apocVibrationEnabled && (
                    <div className="mt-3 grid grid-cols-[1fr_auto] gap-3 items-end">
                      <div>
                        <Label htmlFor="apoc-vibration-volume" className="text-[10px] uppercase tracking-[0.28em] text-white/50">
                          Buzz volume (0–100)
                        </Label>
                        <Input
                          id="apoc-vibration-volume"
                          type="number"
                          inputMode="numeric"
                          min={0}
                          max={100}
                          value={apocVibrationVolume}
                          onChange={(e) => setApocVibrationVolume(e.target.value)}
                          className="mt-2 h-10 bg-black/30 border-white/10 text-white"
                          data-testid="apocalypse-vibration-volume"
                        />
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={() => {
                          // Preview loop for ~2.5 s so the magician can
                          // dial in a volume that carries through their
                          // performance setting without leaving Settings.
                          try {
                            const el = new Audio("/audio/apoc_vibration.mp3");
                            el.volume = Math.max(0, Math.min(1, (parseInt(apocVibrationVolume, 10) || 0) / 100));
                            el.play().catch(() => {});
                            setTimeout(() => { try { el.pause(); } catch { /* ignore */ } }, 2500);
                          } catch { /* ignore */ }
                        }}
                        className="h-10 px-4 text-white/70 hover:text-white hover:bg-white/5 border border-white/10 rounded-lg text-xs"
                        data-testid="apocalypse-vibration-test"
                      >
                        Test buzz
                      </Button>
                    </div>
                  )}
                </div>

                <div className="border-t border-white/10 pt-4">
                  <div className="text-[11px] uppercase tracking-[0.32em] text-white/55">
                    Shake resolution
                  </div>
                  <p className="text-xs text-white/45 mt-2 leading-relaxed">
                    What the spectator&rsquo;s shake does once the error
                    dialog is up.
                  </p>
                  <Select value={apocShakeMode} onValueChange={setApocShakeMode}>
                    <SelectTrigger
                      className="mt-3 bg-black/30 border-white/10 text-white h-10"
                      data-testid="apocalypse-shake-mode"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-neutral-900 border-white/10 text-white">
                      <SelectItem value="one-shake" className="text-white focus:bg-white/10 focus:text-white">
                        One shake &mdash; instantly back to clean Notes
                      </SelectItem>
                      <SelectItem value="two-shake" className="text-white focus:bg-white/10 focus:text-white">
                        Two shakes &mdash; first re-glitches into the 2nd reveal, second cleans up
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  {apocShakeMode === "two-shake" && (
                    <div className="mt-3">
                      <Label htmlFor="apoc-second-template" className="text-[10px] uppercase tracking-[0.28em] text-white/50">
                        Second prediction template
                      </Label>
                      <Input
                        id="apoc-second-template"
                        value={apocSecondTemplate}
                        onChange={(e) => setApocSecondTemplate(e.target.value)}
                        placeholder={DEFAULT_APOCALYPSE_SECOND_TEMPLATE}
                        maxLength={240}
                        className="mt-2 h-10 bg-black/30 border-white/10 text-white"
                        data-testid="apocalypse-second-template-input"
                      />
                      <p className="text-[11px] text-white/40 mt-2 leading-relaxed">
                        Use <code className="text-white/70">(xxx)</code> as a
                        placeholder for the clock-swiped card name. The
                        magician encodes the 2nd card via clock-swipe
                        while the error dialog is showing.
                      </p>
                    </div>
                  )}
                </div>

                {/* ---------- Push notifications master switch ---------- */}
                <div className="border-t border-white/10 pt-4">
                  <div className="flex items-center justify-between gap-4">
                    <div className="min-w-0">
                      <div className="text-[11px] uppercase tracking-[0.32em] text-white/55">
                        Fake push banners
                      </div>
                      <p className="text-xs text-white/45 mt-2 leading-relaxed">
                        Master switch for the fake iOS push-notification
                        banners that overlay the glitch (Mail, Messages,
                        WhatsApp, etc.). Turn off for venues where you&rsquo;d
                        rather keep the screen clean.
                      </p>
                    </div>
                    <Switch
                      checked={apocPushEnabled}
                      onCheckedChange={setApocPushEnabled}
                      data-testid="apocalypse-push-switch"
                    />
                  </div>

                  {apocPushEnabled && (
                    <div className="mt-4 pt-4 border-t border-white/[0.06] flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <Label className="text-[13px] text-white/90 font-medium">
                          Banner sound
                        </Label>
                        <p className="text-xs text-white/45 mt-1 leading-relaxed">
                          Plays an iOS notification tri-tone with every
                          banner. Turn off for silent venues.
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() => {
                            try {
                              // Play one of the real MP3 samples so the
                              // "Test" preview matches exactly what the
                              // spectator hears during an Apocalypse
                              // banner — no synth tell.
                              playAppNotification("messages", { volume: 0.7 });
                            } catch { /* ignore */ }
                          }}
                          disabled={!apocPushSoundEnabled}
                          className="h-9 px-3 text-white/70 hover:text-white hover:bg-white/5 border border-white/10 rounded-lg text-[11px] disabled:opacity-40"
                          data-testid="apocalypse-push-sound-test"
                        >
                          Test
                        </Button>
                        <Switch
                          checked={apocPushSoundEnabled}
                          onCheckedChange={setApocPushSoundEnabled}
                          data-testid="apocalypse-push-sound-switch"
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* ---------- Reveal scroll speed ---------- */}
                <div className="border-t border-white/10 pt-4">
                  <div className="text-[11px] uppercase tracking-[0.32em] text-white/55">
                    Reveal scroll speed
                  </div>
                  <p className="text-xs text-white/45 mt-2 leading-relaxed">
                    How long the list takes to slide each resolving row
                    into view. 0 = instant snap; 2000 = lazy drift.
                  </p>
                  <div className="mt-3 flex items-center gap-3">
                    <Input
                      type="number"
                      inputMode="numeric"
                      min={0}
                      max={2000}
                      value={apocScrollSpeedMs}
                      onChange={(e) => setApocScrollSpeedMs(e.target.value)}
                      className="w-24 h-10 bg-black/30 border-white/10 text-white text-base tabular-nums"
                      data-testid="apocalypse-scroll-speed-input"
                    />
                    <span className="text-sm text-white/55">ms per row</span>
                  </div>
                </div>

                {/* ---------- Post-restore 1-sec flash ---------- */}
                <div className="border-t border-white/10 pt-4">
                  <div className="flex items-center justify-between gap-4">
                    <div className="min-w-0">
                      <div className="text-[11px] uppercase tracking-[0.32em] text-white/55">
                        Post-restore flash
                      </div>
                      <p className="text-xs text-white/45 mt-2 leading-relaxed">
                        After the final shake clears the trick, the preset
                        decoy titles briefly flash the second prediction
                        sentence &mdash; one last gut-punch before normal.
                      </p>
                    </div>
                    <Switch
                      checked={apocRestoreFlashEnabled}
                      onCheckedChange={setApocRestoreFlashEnabled}
                      data-testid="apocalypse-restore-flash-switch"
                    />
                  </div>
                  {apocRestoreFlashEnabled && (
                    <div className="mt-3 flex items-center gap-3">
                      <Input
                        type="number"
                        inputMode="numeric"
                        min={0}
                        max={5000}
                        value={apocRestoreFlashMs}
                        onChange={(e) => setApocRestoreFlashMs(e.target.value)}
                        className="w-24 h-10 bg-black/30 border-white/10 text-white text-base tabular-nums"
                        data-testid="apocalypse-restore-flash-ms-input"
                      />
                      <span className="text-sm text-white/55">ms on-screen</span>
                    </div>
                  )}
                </div>
              </div>

              <Button
                variant="ghost"
                onClick={() => {
                  try { playIphoneLockClick({ volume: 0.9 }); } catch { /* ignore */ }
                  navigate("/lock");
                }}
                className="w-full text-white/75 hover:text-white hover:bg-white/5 rounded-xl h-10 border border-white/10"
                data-testid="apocalypse-preview-btn"
              >
                Preview lockscreen
              </Button>

              {/* Rehearse trick — arms a chosen dummy card and bounces
                  straight to /lock so the magician can run the entire
                  sequence (unlock → glitch → reveal → haunt → shake →
                  reboot) end-to-end. Once the reboot finishes, the
                  one-shot rehearsing flag in localStorage redirects
                  back here, no triple-tap escape needed. */}
              <div
                className="mt-4 rounded-xl border border-white/10 bg-black/20 p-4"
                data-testid="apocalypse-rehearse-block"
              >
                <div className="text-[11px] uppercase tracking-[0.32em] text-white/55">
                  Rehearse trick
                </div>
                <p className="text-xs text-white/45 mt-2 leading-relaxed">
                  One-tap dry run with a dummy card. Plays the full
                  sequence using your current timings, then drops you
                  back here automatically.
                </p>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <Select value={rehearseValue} onValueChange={setRehearseValue}>
                    <SelectTrigger
                      className="bg-black/30 border-white/10 text-white h-10"
                      data-testid="apocalypse-rehearse-value"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-neutral-900 border-white/10 text-white max-h-[60vh]">
                      {[
                        ["A", "Ace"], ["2", "Two"], ["3", "Three"], ["4", "Four"],
                        ["5", "Five"], ["6", "Six"], ["7", "Seven"], ["8", "Eight"],
                        ["9", "Nine"], ["10", "Ten"], ["J", "Jack"], ["Q", "Queen"], ["K", "King"],
                      ].map(([v, label]) => (
                        <SelectItem key={v} value={v} className="text-white focus:bg-white/10 focus:text-white">
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={rehearseSuit} onValueChange={setRehearseSuit}>
                    <SelectTrigger
                      className="bg-black/30 border-white/10 text-white h-10"
                      data-testid="apocalypse-rehearse-suit"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-neutral-900 border-white/10 text-white">
                      {[
                        ["S", "Spades ♠"], ["H", "Hearts ♥"], ["C", "Clubs ♣"], ["D", "Diamonds ♦"],
                      ].map(([s, label]) => (
                        <SelectItem key={s} value={s} className="text-white focus:bg-white/10 focus:text-white">
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  onClick={() => {
                    const delay = Math.max(
                      MIN_APOCALYPSE_DELAY,
                      Math.min(MAX_APOCALYPSE_DELAY, parseInt(apocDelay, 10) || 0)
                    );
                    setApocalypseArmed(
                      { value: rehearseValue, suit: rehearseSuit },
                      delay
                    );
                    setApocalypseRehearsing(true);
                    try { playIphoneLockClick({ volume: 0.9 }); } catch { /* ignore */ }
                    navigate("/lock");
                  }}
                  className="mt-3 w-full bg-white/10 hover:bg-white/15 text-white rounded-xl h-10 border border-white/10"
                  data-testid="apocalypse-rehearse-btn"
                >
                  Rehearse trick
                </Button>
              </div>
            </div>
          )}
        </Card>
  );
}
